import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useClinicStatistics,
  useFinancialSummary,
  useDoctorsPerformance,
  useSpecialistsPerformance,
} from "../../../hooks/useInsights";
import { useGetSpecialists } from "../../../hooks/useShifts"; // reuse existing hooks
import drEasyApi from "../../../Api/Customapi";
import {
  Card,
  Table,
  DatePicker,
  Tooltip,
  Tag,
  Button,
  Progress,
  Select,
  Row,
  Col,
  Grid,
} from "antd";
import { FaBuilding, FaUserMd, FaStethoscope } from "react-icons/fa";
import {
  CalendarOutlined,
  FileExcelOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import XLSX from "xlsx-js-style";
import { BiExport } from "react-icons/bi";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
    const role = localStorage.getItem("role");

// ─── TAB CONFIG ────────────────────────────────────────────────────────────────
const TABS = [
  { key: "clinics",     label: "Clinic Performance",     short: "Clinics",     icon: <FaBuilding /> },
  { key: "specialists", label: "Specialist Performance", short: "Specialists", icon: <FaStethoscope /> },
  { key: "consultants", label: "Consultant Performance", short: "Consultants", icon: <FaUserMd /> },
  role!=7&&{ key: "financial",   label: "Financial",              short: "Financial",   icon: <DollarOutlined /> },
];

// ─── HELPERS ───────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EGP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0);

const pctCell = (value, total) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <div className="flex flex-col items-center">
      <span className="font-medium text-gray-700">{value ?? 0}</span>
      {pct !== null && <span className="text-xs text-gray-400">{pct}%</span>}
    </div>
  );
};

const statusTag = (value, color) => (
  <Tag color={color} className="font-medium text-sm px-2 !m-0">
    {value ?? 0}
  </Tag>
);

const completionRateCell = (rate) => {
  if (rate == null) return <span className="text-gray-400">—</span>;
  const pct = Math.round(rate);
  const color = pct >= 80 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626";
  return (
    <div className="flex flex-col items-center gap-1 min-w-[64px]">
      <span className="font-semibold text-sm" style={{ color }}>{pct}%</span>
      <Progress
        percent={pct}
        size="small"
        showInfo={false}
        strokeColor={color}
        trailColor="#e5e7eb"
        style={{ width: 56, margin: 0 }}
      />
    </div>
  );
};

// On mobile: hide the "#" column, keep only the name column fixed (narrow),
// and shrink the other columns so more data fits on screen.
const adaptColumns = (cols, isMobile) => {
  if (!isMobile) return cols;
  return cols
    .filter((c) => c.title !== "#")
    .map((c) => {
      const isName = c.fixed === "left";
      return {
        ...c,
        fixed: isName ? "left" : undefined,
        width: isName ? 130 : Math.max(Math.round((c.width || 100) * 0.75), 90),
      };
    });
};

const totalWidth = (cols) => cols.reduce((sum, c) => sum + (c.width || 100), 0);

// ─── COLUMN DEFINITIONS ────────────────────────────────────────────────────────
const nameCell = (Icon, text) => (
  <div className="flex items-center gap-2">
    <Icon className="text-primary shrink-0 hidden sm:block" />
    <span className="font-semibold text-primary text-xs sm:text-sm break-words leading-tight">
      {text}
    </span>
  </div>
);

const clinicColumns = [
  {
    title: "#",
    width: 55,
    fixed: "left",
    render: (_, __, i) => <span className="text-gray-500 font-medium">{i + 1}</span>,
  },
  {
    title: "Clinic Name",
    dataIndex: "clinicName",
    key: "clinicName",
    width: 230,
    fixed: "left",
    render: (text) => nameCell(FaBuilding, text),
    sorter: (a, b) => a.clinicName?.localeCompare(b.clinicName),
  },
  {
    title: "Total Bookings",
    dataIndex: "numOfBookings",
    key: "numOfBookings",
    align: "center",
    width: 145,
    render: (v) => <span className="font-medium text-gray-700">{v ?? 0}</span>,
    sorter: (a, b) => a.numOfBookings - b.numOfBookings,
  },
  {
    title: "Completed Bookings",
    dataIndex: "completedBookings",
    key: "completedBookings",
    align: "center",
    width: 175,
    render: (v, r) => pctCell(v, r.numOfBookings),
    sorter: (a, b) => a.completedBookings - b.completedBookings,
  },
  {
    title: "Total Sessions",
    dataIndex: "totalSessions", // normalised from numOfSessions in the hook
    key: "totalSessions",
    align: "center",
    width: 145,
    render: (v) => <span className="font-medium text-gray-700">{v ?? 0}</span>,
    sorter: (a, b) => a.totalSessions - b.totalSessions,
  },
  {
    title: "Completed Sessions",
    dataIndex: "completedSessions",
    key: "completedSessions",
    align: "center",
    width: 175,
    render: (v, r) => pctCell(v, r.totalSessions),
    sorter: (a, b) => a.completedSessions - b.completedSessions,
  },
];

// Specialist columns — maps to SpecilistsPerformace response shape
const specialistColumns = [
  {
    title: "#",
    width: 55,
    fixed: "left",
    render: (_, __, i) => <span className="text-gray-500 font-medium">{i + 1}</span>,
  },
  {
    title: "Specialist Name",
    dataIndex: "specialistName",
    key: "specialistName",
    width: 230,
    fixed: "left",
    render: (text) => nameCell(FaStethoscope, text),
    sorter: (a, b) => a.specialistName?.localeCompare(b.specialistName),
  },
  {
    title: "Total Sessions",
    dataIndex: "totalSessions",
    key: "totalSessions",
    align: "center",
    width: 145,
    render: (v) => <span className="font-medium text-gray-700">{v ?? 0}</span>,
    sorter: (a, b) => a.totalSessions - b.totalSessions,
  },
  {
    title: "Completed",
    dataIndex: "completedSessions",
    key: "completedSessions",
    align: "center",
    width: 130,
    render: (v, r) => pctCell(v, r.totalSessions),
    sorter: (a, b) => a.completedSessions - b.completedSessions,
  },
  {
    title: "Pending",
    dataIndex: "pendingSessions",
    key: "pendingSessions",
    align: "center",
    width: 120,
    render: (v) => statusTag(v, "orange"),
    sorter: (a, b) => a.pendingSessions - b.pendingSessions,
  },
  {
    title: "Cancelled",
    dataIndex: "cancelledSessions",
    key: "cancelledSessions",
    align: "center",
    width: 120,
    render: (v) => statusTag(v, "red"),
    sorter: (a, b) => a.cancelledSessions - b.cancelledSessions,
  },
  {
    title: "Skipped",
    dataIndex: "skippedSessions",
    key: "skippedSessions",
    align: "center",
    width: 110,
    render: (v) => statusTag(v, "default"),
    sorter: (a, b) => a.skippedSessions - b.skippedSessions,
  },
  {
    title: "Completion Rate",
    dataIndex: "completionRate",
    key: "completionRate",
    align: "center",
    width: 145,
    render: (v) => completionRateCell(v),
    sorter: (a, b) => (a.completionRate ?? 0) - (b.completionRate ?? 0),
  },
];

// Consultant columns — maps to DoctorsPerformace response shape
const consultantColumns = [
  {
    title: "#",
    width: 55,
    fixed: "left",
    render: (_, __, i) => <span className="text-gray-500 font-medium">{i + 1}</span>,
  },
  {
    title: "Doctor Name",
    dataIndex: "doctorName",
    key: "doctorName",
    width: 230,
    fixed: "left",
    render: (text) => nameCell(FaUserMd, text),
    sorter: (a, b) => a.doctorName?.localeCompare(b.doctorName),
  },
  {
    title: "Total Bookings",
    dataIndex: "totalBookings",
    key: "totalBookings",
    align: "center",
    width: 145,
    render: (v) => <span className="font-medium text-gray-700">{v ?? 0}</span>,
    sorter: (a, b) => a.totalBookings - b.totalBookings,
  },
  {
    title: "Completed",
    dataIndex: "completedBookings",
    key: "completedBookings",
    align: "center",
    width: 130,
    render: (v, r) => pctCell(v, r.totalBookings),
    sorter: (a, b) => a.completedBookings - b.completedBookings,
  },
  {
    title: "Pending",
    dataIndex: "pendingBookings",
    key: "pendingBookings",
    align: "center",
    width: 120,
    render: (v) => statusTag(v, "orange"),
    sorter: (a, b) => a.pendingBookings - b.pendingBookings,
  },
  {
    title: "Cancelled",
    dataIndex: "cancelledBookings",
    key: "cancelledBookings",
    align: "center",
    width: 120,
    render: (v) => statusTag(v, "red"),
    sorter: (a, b) => a.cancelledBookings - b.cancelledBookings,
  },
  {
    title: "Skipped",
    dataIndex: "skippedBookings",
    key: "skippedBookings",
    align: "center",
    width: 110,
    render: (v) => statusTag(v, "default"),
    sorter: (a, b) => a.skippedBookings - b.skippedBookings,
  },
  {
    title: "Completion Rate",
    dataIndex: "completionRate",
    key: "completionRate",
    align: "center",
    width: 145,
    render: (v) => completionRateCell(v),
    sorter: (a, b) => (a.completionRate ?? 0) - (b.completionRate ?? 0),
  },
];

// Financial columns — maps to ClinicsEarning response shape
const financialColumns = [
  {
    title: "#",
    width: 55,
    fixed: "left",
    render: (_, __, i) => <span className="text-gray-500 font-medium">{i + 1}</span>,
  },
  {
    title: "Clinic Name",
    dataIndex: "clinicName",
    key: "clinicName",
    width: 230,
    fixed: "left",
    render: (text) => nameCell(FaBuilding, text),
    sorter: (a, b) => a.clinicName?.localeCompare(b.clinicName),
  },
  {
    title: "Expected Earnings",
    dataIndex: "expectedEarnings",
    key: "expectedEarnings",
    align: "right",
    width: 180,
    render: (v) => <span className="font-medium text-gray-700">{formatCurrency(v)}</span>,
    sorter: (a, b) => a.expectedEarnings - b.expectedEarnings,
  },
  {
    title: "Actual Paid",
    dataIndex: "actualPaidAmount",
    key: "actualPaidAmount",
    align: "right",
    width: 165,
    render: (v) => <span className="font-semibold text-green-600">{formatCurrency(v)}</span>,
    sorter: (a, b) => a.actualPaidAmount - b.actualPaidAmount,
  },
  {
    title: "Remaining",
    dataIndex: "remainingAmount",
    key: "remainingAmount",
    align: "right",
    width: 160,
    render: (v) => {
      const isNeg = v < 0;
      return (
        <Tooltip
          title={
            isNeg
              ? "Overpaid — client paid more than expected"
              : v === 0
              ? "Fully settled"
              : "Outstanding balance"
          }
        >
          <Tag
            color={isNeg ? "green" : v === 0 ? "default" : "red"}
            className="font-semibold text-sm px-2 !m-0"
          >
            {formatCurrency(v)}
          </Tag>
        </Tooltip>
      );
    },
    sorter: (a, b) => a.remainingAmount - b.remainingAmount,
  },
];

// ─── GENERIC SUMMARY ROW ───────────────────────────────────────────────────────
function SummaryRow({ cells, isMobile }) {
  let idx = 0;
  return (
    <Table.Summary fixed>
      <Table.Summary.Row className="bg-gray-100 font-semibold">
        {cells.map((cell, i) => {
          // On mobile the "#" column is removed, so the "Total" label spans 1 column only
          const span = isMobile ? 1 : cell.colSpan || 1;
          const index = idx;
          idx += span;
          return (
            <Table.Summary.Cell
              key={i}
              index={index}
              align={cell.align || "center"}
              colSpan={span}
            >
              <span className={`${cell.className || "text-gray-900 font-bold"} text-xs sm:text-sm`}>
                {cell.value}
              </span>
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
}

// ─── SUMMARY STAT CARD (for overall summary from API) ─────────────────────────
// eslint-disable-next-line no-unused-vars
function SummaryCards({ data, type }) {
  if (!data) return null;

  const items =
    type === "specialists"
      ? [
          { label: "Total Specialists", value: data.totalSpecialists ?? 0, color: "#0891b2" },
          { label: "Total Sessions",    value: data.totalSessions ?? 0,    color: "#7c3aed" },
          { label: "Completed",         value: data.totalCompletedSessions ?? 0, color: "#16a34a" },
          { label: "Pending",           value: data.totalPendingSessions ?? 0,   color: "#d97706" },
          { label: "Cancelled",         value: data.totalCancelledSessions ?? 0, color: "#dc2626" },
          {
            label: "Completion Rate",
            value: `${Math.round(data.overallCompletionRate ?? 0)}%`,
            color: "#0891b2",
          },
        ]
      : [
          { label: "Total Consultants", value: data.totalDoctors ?? 0,    color: "#0891b2" },
          { label: "Total Bookings",    value: data.totalBookings ?? 0,    color: "#7c3aed" },
          { label: "Completed",         value: data.totalCompletedBookings ?? 0, color: "#16a34a" },
          { label: "Pending",           value: data.totalPendingBookings ?? 0,   color: "#d97706" },
          { label: "Cancelled",         value: data.totalCancelledBookings ?? 0, color: "#dc2626" },
          {
            label: "Completion Rate",
            value: `${Math.round(data.overallCompletionRate ?? 0)}%`,
            color: "#0891b2",
          },
        ];

  return (
    <Row gutter={[12, 12]} className="mb-4">
      {items.map((item) => (
        <Col key={item.label} xs={12} sm={8} md={4}>
          <Card size="small" className="shadow-sm text-center">
            <div className="text-xs text-gray-500 mb-1">{item.label}</div>
            <div className="text-lg font-bold" style={{ color: item.color }}>
              {item.value}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

// ─── EXPORT HELPERS ────────────────────────────────────────────────────────────
function buildAndDownload(rows, sheetName, colWidths, dateRange) {
  console.log(dateRange,"daterange");
  
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const keys = Object.keys(rows[0] || {});
  const headerStyle = {
    font: { bold: true },
    alignment: { horizontal: "center", vertical: "center" },
  };
  const totalStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "16A34A" } },
    alignment: { horizontal: "center", vertical: "center" },
  };

  keys.forEach((_, c) => {
    const hCell = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[hCell]) worksheet[hCell].s = headerStyle;
    const tCell = XLSX.utils.encode_cell({ r: rows.length, c });
    if (worksheet[tCell]) worksheet[tCell].s = totalStyle;
  });

  worksheet["!cols"] = colWidths.map((w) => ({ wch: w }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, worksheet, sheetName);
  const from = dayjs(dateRange.from).format("YYYY-MM-DD");
  const to   = dayjs(dateRange.to).format("YYYY-MM-DD");
  const fromTo=dateRange?.from?`_${from}_to_${to}`:""
  XLSX.writeFile(wb, `${sheetName.replace(/ /g, "_")}_${fromTo}.xlsx`);
}

// ─── MOBILE-ONLY CSS (date picker popup + hidden scrollbar for tabs) ───────────
const responsiveCss = `
  .perf-tabs-scroll { scrollbar-width: none; -ms-overflow-style: none; }
  .perf-tabs-scroll::-webkit-scrollbar { display: none; }

  @media (max-width: 767px) {
    .perf-range-popup { max-width: calc(100vw - 16px); }
    .perf-range-popup .ant-picker-panel-container { max-width: 100%; overflow-x: auto; }
    .perf-range-popup .ant-picker-panels { flex-direction: column; }
    .perf-range-popup .ant-picker-panel { width: 100%; }
    .perf-range-popup .ant-picker-date-panel,
    .perf-range-popup .ant-picker-content { width: 100%; }
    .perf-table .ant-table-thead > tr > th { padding: 8px 6px; font-size: 12px; }
    .perf-table .ant-table-tbody > tr > td { padding: 8px 6px; }
    .perf-table .ant-table-summary > tr > td { padding: 8px 6px; }
  }
`;

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PerformanceDashboard() {
  const screens = useBreakpoint();
  const isMobile = screens.md === false; // < 768px

  const [activeTab, setActiveTab] = useState("clinics");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedDoctorId,     setSelectedDoctorId]     = useState(null);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState(null);

  // ── Lookup lists for the filter selects ──
  const { data: specialistsListData } = useGetSpecialists({ pageSize: 200 });

  const { data: doctorsListData } = useQuery({
    queryKey: ["doctors-list"],
    queryFn: async () => {
      const res = await drEasyApi.get("/api/Centers/getMyDoctors", {
        params: { PageNumber: 1, PageSize: 200 },
      });
      return res.data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const doctorOptions = (doctorsListData?.doctors ?? []).map((d) => ({
    value: d.doctorId,
    label: `Dr. ${d.name}`,
  }));
  const specialistOptions = (specialistsListData?.specialists ?? []).map((s) => ({
    value: s.specialistId,
    label: s.name,
  }));

  // ── Data hooks ──
  const {
    data: clinicStats,
    isLoading: clinicLoading,
    error: clinicError,
  } = useClinicStatistics(dateRange.from, dateRange.to);

  const {
    data: financialData,
    isLoading: financialLoading,
    error: financialError,
  } = useFinancialSummary(dateRange.from, dateRange.to);

  const {
    data: doctorsData,
    isLoading: doctorsLoading,
    error: doctorsError,
  } = useDoctorsPerformance(dateRange.from, dateRange.to, selectedDoctorId);

  const {
    data: specialistsData,
    isLoading: specialistsLoading,
    error: specialistsError,
  } = useSpecialistsPerformance(dateRange.from, dateRange.to, selectedSpecialistId);

  const handleDateChange = (dates) => {
    if (dates?.length === 2) {
      setDateRange({
        from: dates[0].startOf("day").toISOString(),
        to:   dates[1].endOf("day").toISOString(),
      });
    } else {
      setDateRange({ from: null, to: null });
    }
  };

  // ── Derived lists ──
  const clinicList     = clinicStats?.clinics ?? [];
  const financialList  = financialData?.clinics ?? [];
  const doctorList     = doctorsData?.doctors ?? [];
  const specialistList = specialistsData?.specialists ?? [];

  // ── Totals ──
  const clinicTotals = clinicList.reduce(
    (acc, c) => ({
      numOfBookings:     acc.numOfBookings     + (c.numOfBookings     ?? 0),
      completedBookings: acc.completedBookings + (c.completedBookings ?? 0),
      totalSessions:     acc.totalSessions     + (c.totalSessions     ?? 0),
      completedSessions: acc.completedSessions + (c.completedSessions ?? 0),
    }),
    { numOfBookings: 0, completedBookings: 0, totalSessions: 0, completedSessions: 0 }
  );

  const financialTotals = financialList.reduce(
    (acc, c) => ({
      expectedEarnings: acc.expectedEarnings + (c.expectedEarnings ?? 0),
      actualPaidAmount: acc.actualPaidAmount + (c.actualPaidAmount ?? 0),
      remainingAmount:  acc.remainingAmount  + (c.remainingAmount  ?? 0),
    }),
    { expectedEarnings: 0, actualPaidAmount: 0, remainingAmount: 0 }
  );

  // ── Export handlers ──
  const exportClinics = () => {
    if (!clinicList.length) return;
    const rows = clinicList.map((c, i) => ({
      "#": i + 1,
      "Clinic Name":         c.clinicName,
      "Total Bookings":      c.numOfBookings     ?? 0,
      "Completed Bookings":  c.completedBookings ?? 0,
      "Total Sessions":      c.totalSessions     ?? 0,
      "Completed Sessions":  c.completedSessions ?? 0,
    }));
    rows.push({
      "#": "",
      "Clinic Name":        "TOTAL",
      "Total Bookings":      clinicTotals.numOfBookings,
      "Completed Bookings":  clinicTotals.completedBookings,
      "Total Sessions":      clinicTotals.totalSessions,
      "Completed Sessions":  clinicTotals.completedSessions,
    });
    buildAndDownload(rows, "Clinic Performance", [5, 30, 16, 20, 16, 20], dateRange);
  };

  const exportSpecialists = () => {
    if (!specialistList.length) return;
    const rows = specialistList.map((s, i) => ({
      "#": i + 1,
      "Specialist Name":  s.specialistName,
      "Total Sessions":   s.totalSessions     ?? 0,
      "Completed":        s.completedSessions ?? 0,
      "Pending":          s.pendingSessions   ?? 0,
      "Cancelled":        s.cancelledSessions ?? 0,
      "Skipped":          s.skippedSessions   ?? 0,
      "Completion Rate":  `${Math.round(s.completionRate ?? 0)}%`,
    }));
    const sum = specialistsData?.summary;
    if (sum) {
      rows.push({
        "#": "",
        "Specialist Name": "TOTAL",
        "Total Sessions":   sum.totalSessions          ?? 0,
        "Completed":        sum.totalCompletedSessions ?? 0,
        "Pending":          sum.totalPendingSessions   ?? 0,
        "Cancelled":        sum.totalCancelledSessions ?? 0,
        "Skipped":          sum.totalSkippedSessions   ?? 0,
        "Completion Rate":  `${Math.round(sum.overallCompletionRate ?? 0)}%`,
      });
    }
    buildAndDownload(rows, "Specialist Performance", [5, 30, 16, 14, 14, 14, 12, 16], dateRange);
  };

  const exportConsultants = () => {
    if (!doctorList.length) return;
    const rows = doctorList.map((d, i) => ({
      "#": i + 1,
      "Doctor Name":     d.doctorName,
      "Total Bookings":  d.totalBookings     ?? 0,
      "Completed":       d.completedBookings ?? 0,
      "Pending":         d.pendingBookings   ?? 0,
      "Cancelled":       d.cancelledBookings ?? 0,
      "Skipped":         d.skippedBookings   ?? 0,
      "Completion Rate": `${Math.round(d.completionRate ?? 0)}%`,
    }));
    const sum = doctorsData?.summary;
    if (sum) {
      rows.push({
        "#": "",
        "Doctor Name":    "TOTAL",
        "Total Bookings":  sum.totalBookings          ?? 0,
        "Completed":       sum.totalCompletedBookings ?? 0,
        "Pending":         sum.totalPendingBookings   ?? 0,
        "Cancelled":       sum.totalCancelledBookings ?? 0,
        "Skipped":         sum.totalSkippedBookings   ?? 0,
        "Completion Rate": `${Math.round(sum.overallCompletionRate ?? 0)}%`,
      });
    }
    buildAndDownload(rows, "Consultant Performance", [5, 30, 16, 14, 14, 14, 12, 16], dateRange);
  };

  const exportFinancial = () => {
    if (!financialList.length) return;
    const rows = financialList.map((c, i) => ({
      "#": i + 1,
      "Clinic Name":             c.clinicName,
      "Expected Earnings (EGP)": c.expectedEarnings ?? 0,
      "Actual Paid (EGP)":       c.actualPaidAmount ?? 0,
      "Remaining (EGP)":         c.remainingAmount  ?? 0,
    }));
    rows.push({
      "#": "",
      "Clinic Name":             "TOTAL",
      "Expected Earnings (EGP)": financialTotals.expectedEarnings,
      "Actual Paid (EGP)":       financialTotals.actualPaidAmount,
      "Remaining (EGP)":         financialTotals.remainingAmount,
    });
    buildAndDownload(rows, "Financial Summary", [5, 30, 22, 18, 16], dateRange);
  };

  // ── Shared Select props ──
  const filterOption = (input, opt) =>
    (opt?.label ?? "").toLowerCase().includes(input.toLowerCase());

  // ── Tab content config ──
  const tabContent = {
    clinics: {
      columns:   clinicColumns,
      data:      clinicList,
      loading:   clinicLoading,
      error:     clinicError,
      rowKey:    "clinicId",
      emptyText: "No clinic statistics available for this period",
      exportFn:  exportClinics,
      extraHeader: null,
      summary:
        clinicList.length > 0 ? (
          <SummaryRow
            isMobile={isMobile}
            cells={[
              { value: "Total", colSpan: 2, align: "left" },
              { value: clinicTotals.numOfBookings },
              { value: clinicTotals.completedBookings },
              { value: clinicTotals.totalSessions },
              { value: clinicTotals.completedSessions },
            ]}
          />
        ) : null,
    },

    specialists: {
      columns:   specialistColumns,
      data:      specialistList,
      loading:   specialistsLoading,
      error:     specialistsError,
      rowKey:    "specialistId",
      emptyText: "No specialist statistics available for this period",
      exportFn:  exportSpecialists,
      extraHeader: (
        <div className="w-full sm:max-w-xl mb-3">
          <div className="text-sm text-gray-600 mb-1">Filter by Specialist</div>
          <Select
            showSearch
            allowClear
            placeholder="All specialists"
            size={isMobile ? "middle" : "large"}
            className="w-full"
            value={selectedSpecialistId}
            onChange={(val) => setSelectedSpecialistId(val ?? null)}
            options={specialistOptions}
            filterOption={filterOption}
            notFoundContent="No specialists found"
          />
        </div>
      ),
      summary:
        specialistList.length > 0 && specialistsData?.summary ? (
          <SummaryRow
            isMobile={isMobile}
            cells={[
              { value: "Total", colSpan: 2, align: "left" },
              { value: specialistsData.summary.totalSessions          ?? 0 },
              { value: specialistsData.summary.totalCompletedSessions ?? 0 },
              { value: specialistsData.summary.totalPendingSessions   ?? 0 },
              { value: specialistsData.summary.totalCancelledSessions ?? 0 },
              { value: specialistsData.summary.totalSkippedSessions   ?? 0 },
              {
                value: `${Math.round(specialistsData.summary.overallCompletionRate ?? 0)}%`,
                className: "text-primary font-bold",
              },
            ]}
          />
        ) : null,
    },

    consultants: {
      columns:   consultantColumns,
      data:      doctorList,
      loading:   doctorsLoading,
      error:     doctorsError,
      rowKey:    "doctorId",
      emptyText: "No consultant statistics available for this period",
      exportFn:  exportConsultants,
      extraHeader: (
        <div className="w-full sm:max-w-xl mb-3">
          <div className="text-sm text-gray-600 mb-1">Filter by Doctor</div>
          <Select
            showSearch
            allowClear
            placeholder="All doctors"
            size={isMobile ? "middle" : "large"}
            className="w-full"
            value={selectedDoctorId}
            onChange={(val) => setSelectedDoctorId(val ?? null)}
            options={doctorOptions}
            filterOption={filterOption}
            notFoundContent="No doctors found"
          />
        </div>
      ),
      summary:
        doctorList.length > 0 && doctorsData?.summary ? (
          <SummaryRow
            isMobile={isMobile}
            cells={[
              { value: "Total", colSpan: 2, align: "left" },
              { value: doctorsData.summary.totalBookings          ?? 0 },
              { value: doctorsData.summary.totalCompletedBookings ?? 0 },
              { value: doctorsData.summary.totalPendingBookings   ?? 0 },
              { value: doctorsData.summary.totalCancelledBookings ?? 0 },
              { value: doctorsData.summary.totalSkippedBookings   ?? 0 },
              {
                value: `${Math.round(doctorsData.summary.overallCompletionRate ?? 0)}%`,
                className: "text-primary font-bold",
              },
            ]}
          />
        ) : null,
    },

    financial: {
      columns:   financialColumns,
      data:      financialList,
      loading:   financialLoading,
      error:     financialError,
      rowKey:    "clinicId",
      emptyText: "No financial data available for this period",
      exportFn:  exportFinancial,
      extraHeader: null,
      summary:
        financialList.length > 0 ? (
          <SummaryRow
            isMobile={isMobile}
            cells={[
              { value: "Total", colSpan: 2, align: "left" },
              { value: formatCurrency(financialTotals.expectedEarnings), align: "right" },
              {
                value: formatCurrency(financialTotals.actualPaidAmount),
                align: "right",
                className: "text-green-600 font-bold",
              },
              {
                value: formatCurrency(financialTotals.remainingAmount),
                align: "right",
                className:
                  financialTotals.remainingAmount < 0
                    ? "text-green-600 font-bold"
                    : financialTotals.remainingAmount === 0
                    ? "text-gray-600 font-bold"
                    : "text-red-600 font-bold",
              },
            ]}
          />
        ) : null,
    },
  };

  const active = tabContent[activeTab];

  const columns = useMemo(
    () => adaptColumns(active.columns, isMobile),
    [active.columns, isMobile]
  );

  return (
    <div className="md:p-2  bg-[#F7FCFD] min-h-screen overflow-x-hidden">
      <style>{responsiveCss}</style>

   

      {/* Tabs + Table */}
      <Card className="shadow-sm [&_.ant-card-body]:!p-3 md:[&_.ant-card-body]:!p-6">
        
        {/* Tab Bar — horizontally scrollable on mobile */}
        <div className="perf-tabs-scroll pb-1 flex flex-wrap gap-1 overflow-x-auto border-b border-gray-200 mb-3 md:mb-4 -mx-3 px-3 md:-mx-6 md:px-6">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key !== "consultants") setSelectedDoctorId(null);
                  if (tab.key !== "specialists") setSelectedSpecialistId(null);
                }}
                className={[
                  "flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm font-medium rounded-t-lg border-b-2 transition-all whitespace-nowrap shrink-0",
                  isActive
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                ].join(" ")}
              >
                <span className="text-sm md:text-base">{tab.icon}</span>
                {isMobile ? tab.short : tab.label}
              </button>
            );
          })}
     
          <div className="w-full flex items-center gap-3 sm:w-auto mt-1">
            <RangePicker
              value={
                dateRange.from && dateRange.to
                  ? [dayjs(dateRange.from), dayjs(dateRange.to)]
                  : null
              }
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              size={isMobile ? "small" : "middle"}
              allowClear
              className="w-full"
              popupClassName="perf-range-popup"
              suffixIcon={<CalendarOutlined />}
            />
            <Tooltip title="Export to excel">
               <Button
            icon={<BiExport />}
            onClick={active.exportFn}
            disabled={!active.data?.length || active.loading}
            style={{ borderColor: "#16a34a", color: "#16a34a" }}
            size={isMobile ? "small" : "middle"}
            block={isMobile}
          >
         
          </Button>
            </Tooltip>
           
          </div>
        </div>

        {/* Per-tab filter */}
        {active.extraHeader}

        {/* Table */}
        {active.error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-sm text-red-700">
              Failed to load data. Please try again.
            </p>
          </div>
        ) : (
          <Table
            className="perf-table"
            columns={columns}
            dataSource={active.data}
            loading={active.loading}
            rowKey={active.rowKey}
            bordered
            size={isMobile ? "small" : "middle"}
            scroll={{
              x: totalWidth(columns),
              y: isMobile ? undefined : 520,
            }}
            rowClassName={(_, index) => (index % 2 === 0 ? "bg-gray-50" : "")}
            pagination={{
              pageSize: 10,
              size: isMobile ? "small" : "default",
              simple: isMobile,
              showTotal: isMobile ? undefined : (total) => `Total ${total} records`,
              showSizeChanger: !isMobile,
              pageSizeOptions: ["10", "20", "50"],
            }}
            locale={{ emptyText: active.emptyText }}
            summary={() => active.summary}
          />
        )}

    
      </Card>
    </div>
  );
}